import React, { useCallback, useEffect } from 'react';

import { CameraOff } from '@yandex-360/iconostasis/dist/icons/CameraOff';
import { MediaLimitationReason } from '@yandex-video-platform/goloom-sdk';
import cn from 'classnames';
import { useSelector } from 'react-redux';

import { SELF } from '../../../../shared/constants';
import { selectIsTestplane } from '../../../../shared/features/flags/selectors';
import { i18n } from '../../../../shared/helpers/i18n';
import { countMetrika } from '../../../../shared/helpers/metrika';
import { Participant } from '../../types';
import { ReactionIcon, StatusIcon } from './components';
import styles from './styles.css';

interface Props {
    participant: Participant;
    showTooltip?: boolean;
    isBadNetworkQuality?: boolean;
    videoLimitation?: false | MediaLimitationReason;
    isMainSlot?: boolean;
}

export const ParticipantStatuses: React.FC<Props> = ({
    participant,
    isBadNetworkQuality,
    videoLimitation,
    showTooltip = true,
    isMainSlot = false,
}) => {
    const isTestplane = useSelector(selectIsTestplane);
    const videoLimitationText = (() => {
        if (videoLimitation === MediaLimitationReason.BY_USER) {
            return i18n('call', 'video-limited-reason-self');
        }

        if (videoLimitation === MediaLimitationReason.BANDWIDTH) {
            return i18n('call', 'video-limited-reason-network');
        }

        return i18n('call', 'video-limited-reason-room-size');
    })();

    const isSelf = participant.id === SELF;

    useEffect(() => {
        countMetrika(
            'meeting_screen',
            isSelf ? 'low_connection_quality' : 'low_connection_quality_another_user',
            isBadNetworkQuality ? 'detected' : 'restored'
        );
    }, [isSelf, isBadNetworkQuality]);

    const handleShowTooltipNetworkScore = useCallback(() => {
        countMetrika(
            'meeting_screen',
            isSelf ? 'low_connection_quality_hover' : 'low_connection_quality_another_user_hover'
        );
    }, [isSelf]);

    const reactionType = participant?.reaction?.reactionType;

    return (
        <>
            {!isTestplane && (
                <div className={cn(styles.container, 'participantStatuses')}>
                    {isBadNetworkQuality ? (
                        <StatusIcon
                            showTooltip={showTooltip}
                            tooltipContent={
                                isSelf
                                    ? i18n('call', 'network-quality-status-bad-self')
                                    : i18n('call', 'network-quality-status-bad-another')
                            }
                            onShowTooltip={handleShowTooltipNetworkScore}
                            imageName="network"
                            className={styles.element}
                        />
                    ) : null}
                    {videoLimitation ? (
                        <StatusIcon
                            showTooltip={showTooltip}
                            tooltipContent={videoLimitationText}
                            icon={<CameraOff variant="solid" size="sm" />}
                            className={styles.element}
                        />
                    ) : null}
                </div>
            )}
            {reactionType && <ReactionIcon isMainSlot={isMainSlot} reactionType={reactionType} />}
        </>
    );
};
